import {
  getFragmentData,
  execute,
  ProductFragment,
  GetProductBySkuDocument,
  GetProductCertificatesDocument,
  GetProductDetailsPageDocument,
  ProductFragmentDoc,
  GetProductCareProgramDocument,
  ProductReviewFragmentDoc,
  ProductReviewFragment,
  GetProductsDocument,
  QuestionFragmentDoc,
  QuestionFragment
} from '@/fo/api/magento';

const getProductBySku = async (
  sku: string
): Promise<ProductFragment | null | undefined> => {
  const result = await execute(GetProductBySkuDocument, {
    sku
  });

  if (result.data?.products?.items?.[0]) {
    return getFragmentData(
      ProductFragmentDoc,
      result.data?.products?.items?.[0]
    );
  }
  return null;
};

const getProductsBySkus = async (
  skus: string[]
): Promise<(ProductFragment | null)[]> => {
  if (skus.length === 0) return [];

  const result = await execute(GetProductsDocument, {
    filter: { sku: { in: skus } },
    pageSize: skus.length,
    page: 1
  });

  const products = result.data?.products?.items?.filter(
    (product) => product !== null
  ) as ProductFragment[];

  return skus.map(
    (sku) => products.find((product) => product.sku === sku) || null
  );
};

const getProductDetailsPage = async (
  productId: string
): Promise<
  | {
      product: ProductFragment;
      reviews: ProductReviewFragment[];
      questions: QuestionFragment[];
    }
  | null
  | undefined
> => {
  const result = await execute(GetProductDetailsPageDocument, {
    productId
  });

  if (result.data?.products?.items?.[0]) {
    return {
      product: getFragmentData(
        ProductFragmentDoc,
        result.data?.products?.items?.[0]
      ),
      reviews: result.data?.products?.items?.[0]?.reviews?.items
        .map((item) => getFragmentData(ProductReviewFragmentDoc, item))
        .filter((item) => item !== null),
      questions: result.data?.products?.items?.[0]?.questions?.items
        .map((item) => getFragmentData(QuestionFragmentDoc, item))
        .filter((item) => item !== null)
    };
  }
  return null;
};

const getCareProgram = async (
  productId: string
): Promise<ProductFragment[]> => {
  const result = await execute(GetProductCareProgramDocument, {
    productId
  });

  if (result.data?.productSet?.program) {
    return result.data?.productSet?.program
      .map((program) => getFragmentData(ProductFragmentDoc, program))
      .filter((program) => program !== null);
  }
  return [];
};

const getCertificatesOfConformity = async (): Promise<
  { name: string; gamma: string; brand: string; certificate: string }[]
> => {
  const result = await execute(GetProductCertificatesDocument, {});

  if (result.data?.productCertificates?.items) {
    return result.data.productCertificates.items
      .map((item) => {
        if (!item?.name || !item?.brand || !item?.certificate) {
          return null;
        }

        return {
          name: item.name,
          gamma: item.gamma?.trim() ?? '',
          brand: item.brand,
          certificate: item.certificate
        };
      })
      .filter(
        (
          item
        ): item is {
          name: string;
          gamma: string;
          brand: string;
          certificate: string;
        } => item !== null
      );
  }

  return [];
};

export const productService = {
  getProductBySku,
  getProductsBySkus,
  getProductDetailsPage,
  getCareProgram,
  getCertificatesOfConformity
};
